// Call plugin.
import pluginInterface from '../interfaceNew'
import * as sagas from './sagas'
import events from '../interfaceNew/events'
import parseConfigs, { fixIceServerUrls, mergeDefaults } from '../configs'

// Other plugins.
import { mapEvents } from '../../events/interface/actions'
import { update as updateConfig, setSdpHandlers } from '../../config/interface/actions'
import { logManager } from '../../logs'

// Helpers.
import { autoRestart } from '../../common/utils'

// Libraries.
import { values } from 'lodash/fp'
import { fork, put } from 'redux-saga/effects'
import { getWebRTCSupportCapabilities } from 'kandy-webrtc'

const log = logManager.getLogger('CALL')

// eslint-disable-next-line no-warning-comments
/**
 * Configuration options for the call feature.
 * @public
 * @name config.call
 * @memberof config
 * @instance
 * @param {Object} call The call configuration object.
 * @param {call.RTCPeerConnectionConfig} [call.defaultPeerConfig] A key-value dictionary that corresponds
 *    to the available RTCPeerConfiguration which is normally passed when creating an RTCPeerConnection.
 *    See {@link https://developer.mozilla.org/en-US/docs/Web/API/RTCPeerConnection/RTCPeerConnection#parameters RTCPeerConnection's configuration parameters} for more information.
 *    This is the recommended way of setting ICE servers and other RTCPeerConnection-related configuration.
 * @param {number} [call.iceCollectionIdealTimeout=1000] The amount of time to wait for an ideal candidate in milliseconds.
 *    The default is 1000ms. An ideal list of candidates is a complete list of candidates considering the RTCPeerConnection configuration.
 *    Note that this values will not be considered if a custom function is passed through the `iceCollectionCheckFunction`, and
 *    any timeouts must be handled by the custom function.
 * @param {number} [call.iceCollectionMaxTimeout=3000] The maximum amount of time to wait for ICE collection in milliseconds.
 *    The default is 3000ms. After this time has been reached, the call will proceed with the currently gathered candidates.
 *    Note that this values will not be considered if a custom function is passed through the `iceCollectionCheckFunction`, and
 *    any timeouts must be handled by the custom function.
 * @param {Function} [call.iceCollectionCheckFunction] Override the default IceCollectionCheckFunction to manually decide when
 *    to proceed with operations, error out, or wait for the appropriate states and candidates. The function will an object containing
 *    the ice collection info. See {@link call.IceCollectionInfo IceCollectionInfo} for more details. The function must return
 *    a results object with details on how to proceed with the ICE collection check or operatiaon. See {@link call.IceCollectionCheckResult IceCollectionResult}
 *    object for details on the format of the return object. See {@link call.IceCollectionCheckFunction IceCollectionCheckFunction} for
 *    more information on the form of the function, as well as information about the default IceCollectionCheckFunction that is used if nothing is provided.
 * @param {boolean} [call.serverTurnCredentials=true] Whether server-provided TURN credentials should be used.
 * @param {Array<call.SdpHandlerFunction>} [call.sdpHandlers] List of SDP handler functions to modify SDP. Advanced usage.
 * @param {boolean} [call.removeH264Codecs=true] Whether to remove "H264" codec lines from incoming and outgoing SDP messages.
 * @param {boolean} [call.earlyMedia=false] Whether early media should be supported for calls. Not supported on Firefox.
 * @param {boolean} [call.resyncOnConnect=false] Whether the SDK should re-sync all call states after connecting (requires WebRTC Gateway 4.7.1+).
 * @param {boolean} [call.mediaBrokerOnly=false] Whether all Calls will be anchored on the MediaBroker instead of being peer-to-peer. Set to true if the backend is configured for broker only mode.
 * @param {boolean} [call.removeBundling=false] Whether to remove a=group attributes to stop media bundling from incoming and outgoing SDP messages.
 * @param {string} [call.ringingFeedbackMode='auto'] The mode for sending ringing feedback to the Caller ('auto', 'manual').
 *    By default, feedback will be automatically sent when a call has been received. In 'manual' mode, the application
 *    must initiate the feedback being sent. See the `call.sendRingingFeedback` API for more info.
 * @param {number} [call.callAuditTimer=25000] Time interval, in milliseconds between call audits.
 * @param {number} [call.mediaConnectionRetryDelay=3000] Delay, in milliseconds for the passive side of a call to wait before trying a media reconnection.
 * @param {boolean} [call.normalizeDestination=true] Specifies whether or not SIP address normalization will be applied.
 */

/**
 * @private
 * @name config.call
 * @memberof config
 * @instance
 * @param {string} [trickleIceMode='NONE'] The Trickle ICE method to use for calls. Currently, no mode is supported.
 */

export default function callsLink (options = {}) {
  const { mediaDevices, peerConnection } = getWebRTCSupportCapabilities()
  if (!mediaDevices || !peerConnection) {
    log.warn('Calls are not supported on this platform due to lack of WebRTC support. Call APIs will not be available.')
    return
  }

  // Parse the options provided by the application and make any adjustments needed
  //    (for backwards-compatibility reasons).
  options = parseConfigs(options)
  // Then merge the defaults into them and validate the values.
  options = mergeDefaults(options)

  function * init ({ webRTC }) {
    // Change sdpSemantics if not supported
    const isPlanB = options.defaultPeerConfig.sdpSemantics === 'plan-b'
    const browserDetails = webRTC.getBrowserDetails()

    // SDP Semantic 'Plan-B' is only supported on Chrome and only prior to M93.
    // If the configs say to use Plan-B, but the browser does not support it,
    //    change to Unified-Plan automatically to prevent potential issues.
    const noPlanBSupport =
      browserDetails.browser !== 'chrome' || (browserDetails.browser === 'chrome' && browserDetails.version >= 93)

    if (isPlanB && noPlanBSupport) {
      log.warn('Only Chrome prior to M93 supports `plan-b` sdpSemantics. Switching to `unified-plan`.')
      options.defaultPeerConfig.sdpSemantics = 'unified-plan'
    }

    /*
     * Firefox does not support pranswer, so it can't receive early media.
     * Ref: https://bugzilla.mozilla.org/show_bug.cgi?id=1004510
     */
    if (browserDetails.browser === 'firefox' && options.earlyMedia === true) {
      log.warn('Firefox does not support pranswer for Early Media. Disabling early media configuration.')
      options.earlyMedia = false
    }

    /*
     * Chrome 110+ will enforce STUN/TURN URL according to RFC Spec.
     * Ref: https://groups.google.com/g/discuss-webrtc/c/L0qDWipd7VE?pli=1
     */
    if (browserDetails.browser === 'chrome' && browserDetails.version >= 110 && options.defaultPeerConfig.iceServers) {
      options.defaultPeerConfig.iceServers.forEach(iceServer => {
        if (Array.isArray(iceServer.urls)) {
          iceServer.urls = iceServer.urls.map(fixIceServerUrls)
        } else if (iceServer.urls) {
          iceServer.urls = fixIceServerUrls(iceServer.urls)
        }
      })
    }

    yield put(updateConfig(options, pluginInterface.name))
    yield put(mapEvents(events))

    // Update the SDP handlers in the config (user provided + some of our own)
    const sdpHandlerOptions = {
      removeH264Codecs: options.removeH264Codecs
    }
    yield put(setSdpHandlers(options.sdpHandlers, sdpHandlerOptions))

    // Dependencies to be provided to every call saga.
    const deps = {
      webRTC: webRTC.managers,
      browserDetails: webRTC.getBrowserDetails
    }

    // Wrap the call sagas in a function that provides them with the webRTC stack.
    const wrappedSagas = values(sagas).map(saga => {
      return autoRestart(() => saga(deps))
    })

    // Run all of the sagas.
    for (const saga of wrappedSagas) {
      yield fork(saga)
    }
  }

  return {
    name: pluginInterface.name,
    api: pluginInterface.api,
    reducer: pluginInterface.reducer,
    init,
    capabilities: ['call', 'link_call', 'link_user_id', 'link_pstn']
  }
}
